/**
 * Admin Page
 * Manage Users, Aircraft, and Trips
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/services/api';
import { Card, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  UserGroupIcon,
  PaperAirplaneIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  PencilIcon,
  XMarkIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import PilotQualificationsPanel from '@/components/PilotQualificationsPanel';

type Tab = 'users' | 'aircraft' | 'trips';

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  baseRate?: number;
  overtimeRate?: number;
  shortNoticeRate?: number;
  currentPin?: string;
  licenceNumber?: string;
  licenceType?: string;
  dateOfBirth?: string;
  medicalCategory?: string;
  medicalIssueDate?: string;
  medicalExpiryDate?: string;
  homeBase?: string;
}

interface Aircraft {
  id: number;
  tailNumber: string;
  aircraftType: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  crewSize: number;
  isActive: boolean;
}

interface Trip {
  id: number;
  qNumber: string;
  startDate: string;
  endDate: string;
  tailNumber?: string;
  aircraftType?: string;
  status: string;
  flights: any[];
  crewMembers: any[];
}

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab) || 'users';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [loading, setLoading] = useState(false);

  // Data
  const [users, setUsers] = useState<User[]>([]);
  const [aircraft, setAircraft] = useState<Aircraft[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [loadError, setLoadError] = useState('');
  const [qualsPilot, setQualsPilot] = useState<User | null>(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      if (activeTab === 'users') {
        const res = await api.get<{ data: User[] }>('/users');
        setUsers(res.data);
      } else if (activeTab === 'aircraft') {
        const res = await api.get<{ data: Aircraft[] }>('/aircraft');
        setAircraft(res.data);
      } else if (activeTab === 'trips') {
        const res = await api.get<{ data: Trip[] }>('/trips');
        setTrips(res.data);
      }
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const openEditModal = (item: any) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const tabs = [
    { id: 'users' as Tab, label: 'Users', icon: UserGroupIcon },
    { id: 'aircraft' as Tab, label: 'Aircraft', icon: PaperAirplaneIcon },
    { id: 'trips' as Tab, label: 'Trips', icon: ClipboardDocumentListIcon },
  ];

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">Admin Panel</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="mb-4 flex justify-end">
        <Button onClick={openCreateModal} icon={<PlusIcon className="w-4 h-4" />}>
          Add {activeTab === 'users' ? 'User' : activeTab === 'aircraft' ? 'Aircraft' : 'Trip'}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : loadError ? (
        <div className="text-center py-12">
          <p className="text-danger font-medium mb-2">Failed to load data</p>
          <p className="text-muted-foreground text-sm mb-4">{loadError}</p>
          <Button variant="secondary" onClick={loadData}>Try Again</Button>
        </div>
      ) : (
        <>
          {activeTab === 'users' && (
            <UsersTable users={users} onEdit={openEditModal} onViewQuals={(u) => setQualsPilot(u)} />
          )}
          {activeTab === 'aircraft' && (
            <AircraftTable aircraft={aircraft} onEdit={openEditModal} />
          )}
          {activeTab === 'trips' && (
            <TripsTable trips={trips} onEdit={openEditModal} />
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <Modal
          type={activeTab}
          item={editingItem}
          users={users}
          aircraft={aircraft}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            loadData();
          }}
        />
      )}

      {/* Qualifications Panel */}
      {qualsPilot && (
        <PilotQualificationsPanel
          pilotId={qualsPilot.id}
          pilotName={`${qualsPilot.firstName} ${qualsPilot.lastName}`}
          onClose={() => setQualsPilot(null)}
        />
      )}
    </div>
  );
}

// Users Table Component
function UsersTable({ users, onEdit, onViewQuals }: { users: User[]; onEdit: (u: User) => void; onViewQuals: (u: User) => void }) {
  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Username</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium">PIN</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Rates</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Medical</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No users found</td></tr>
              ) : users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="font-medium">{user.firstName} {user.lastName}</div>
                    <div className="text-sm text-muted-foreground">{user.email}</div>
                  </td>
                  <td className="px-4 py-3">{user.username}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-400' :
                      user.role === 'pilot' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{user.role === 'pilot' ? '******' : '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {user.role === 'pilot' && (
                      <div>
                        <div>Base: ${user.baseRate}</div>
                        <div>OT: ${user.overtimeRate}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {user.role === 'pilot' && (() => {
                      if (!user.medicalExpiryDate) return <span className="text-muted-foreground">-</span>;
                      const days = Math.ceil((new Date(user.medicalExpiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                      const color = days < 0 ? 'text-red-600 dark:text-red-400'
                        : days < 30 ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-green-600 dark:text-green-400';
                      const label = days < 0 ? 'Expired' : `${days}d`;
                      return <span className={`font-medium ${color}`}>Cat {user.medicalCategory || '?'} · {label}</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right flex justify-end gap-1">
                    {user.role === 'pilot' && (
                      <Button variant="ghost" size="sm" onClick={() => onViewQuals(user)} title="Qualifications">
                        <AcademicCapIcon className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => onEdit(user)}>
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// Aircraft Table Component
function AircraftTable({ aircraft, onEdit }: { aircraft: Aircraft[]; onEdit: (a: Aircraft) => void }) {
  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Tail Number</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Manufacturer / Model</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Year</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Crew Size</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {aircraft.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No aircraft in fleet</td></tr>
              ) : aircraft.map((ac) => (
                <tr key={ac.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono font-medium">{ac.tailNumber}</td>
                  <td className="px-4 py-3">{ac.aircraftType}</td>
                  <td className="px-4 py-3">
                    {ac.manufacturer} {ac.model}
                  </td>
                  <td className="px-4 py-3">{ac.year || '-'}</td>
                  <td className="px-4 py-3">{ac.crewSize}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      ac.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400'
                    }`}>
                      {ac.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(ac)}>
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// Trips Table Component
function TripsTable({ trips, onEdit }: { trips: Trip[]; onEdit: (t: Trip) => void }) {
  const formatDate = (d: string) => {
    const [y, m, day] = d.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString();
  };

  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Q-Number</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Dates</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Aircraft</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Crew</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Flights</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {trips.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No trips found</td></tr>
              ) : trips.map((trip) => (
                <tr key={trip.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono font-medium">{trip.qNumber}</td>
                  <td className="px-4 py-3 text-sm">
                    {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                  </td>
                  <td className="px-4 py-3">
                    {trip.tailNumber || '-'}
                    {trip.aircraftType && <span className="text-muted-foreground text-sm ml-1">({trip.aircraftType})</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {trip.crewMembers.map(cm => `${cm.user.firstName} ${cm.user.lastName}`).join(', ') || '-'}
                  </td>
                  <td className="px-4 py-3">{trip.flights.length} legs</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      trip.status === 'COMPLETED' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400' :
                      trip.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400' :
                      trip.status === 'CANCELLED' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400'
                    }`}>
                      {trip.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(trip)}>
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// Modal Component for Create/Edit
function Modal({
  type,
  item,
  users,
  aircraft,
  onClose,
  onSave,
}: {
  type: Tab;
  item: any;
  users: User[];
  aircraft: Aircraft[];
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState<any>(item || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (type === 'users') {
        if (item) {
          await api.put(`/users/${item.id}`, formData);
        } else {
          await api.post('/users', formData);
        }
      } else if (type === 'aircraft') {
        if (item) {
          await api.put(`/aircraft/${item.id}`, formData);
        } else {
          await api.post('/aircraft', formData);
        }
      } else if (type === 'trips') {
        if (item) {
          // Update trip status only for now
          await api.patch(`/trips/${item.id}/status`, { status: formData.status });
        } else {
          await api.post('/trips', {
            ...formData,
            crewMemberIds: formData.crewMemberIds || [],
            flights: formData.flights || []
          });
        }
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">
            {item ? 'Edit' : 'Create'} {type === 'users' ? 'User' : type === 'aircraft' ? 'Aircraft' : 'Trip'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded text-sm">{error}</div>
          )}

          {type === 'users' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  value={formData.firstName || ''}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  required
                />
                <Input
                  label="Last Name"
                  value={formData.lastName || ''}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  required
                />
              </div>
              <Input
                label="Username"
                value={formData.username || ''}
                onChange={(e) => updateField('username', e.target.value)}
                required
                disabled={!!item}
              />
              <Input
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                required
              />
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={formData.role || 'pilot'}
                  onChange={(e) => updateField('role', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md"
                >
                  <option value="pilot">Pilot</option>
                  <option value="admin">Admin</option>
                  <option value="scheduler">Scheduler</option>
                </select>
              </div>
              {(formData.role === 'pilot' || !formData.role) && (
                <>
                  <Input
                    label="PIN (5 digits)"
                    value={formData.pin || ''}
                    onChange={(e) => updateField('pin', e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    placeholder="12345"
                  />
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="Base Rate ($)"
                      type="number"
                      value={formData.baseRate || ''}
                      onChange={(e) => updateField('baseRate', parseFloat(e.target.value))}
                    />
                    <Input
                      label="OT Rate ($)"
                      type="number"
                      value={formData.overtimeRate || ''}
                      onChange={(e) => updateField('overtimeRate', parseFloat(e.target.value))}
                    />
                    <Input
                      label="Short Notice ($)"
                      type="number"
                      value={formData.shortNoticeRate || ''}
                      onChange={(e) => updateField('shortNoticeRate', parseFloat(e.target.value))}
                    />
                  </div>
                </>
              )}
              {/* Qualifications - Pilots Only */}
              {(formData.role === 'pilot' || !formData.role) && (
                <>
                  <div className="border-t border-border pt-4 mt-2">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Pilot Qualifications</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Licence #"
                      value={formData.licenceNumber || ''}
                      onChange={(e) => updateField('licenceNumber', e.target.value)}
                      placeholder="123456"
                    />
                    <div>
                      <label className="block text-sm font-medium mb-1">Licence Type</label>
                      <select
                        value={formData.licenceType || ''}
                        onChange={(e) => updateField('licenceType', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                      >
                        <option value="">Select</option>
                        <option value="CPL">CPL</option>
                        <option value="ATPL">ATPL</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Date of Birth"
                      type="date"
                      value={formData.dateOfBirth?.split('T')[0] || ''}
                      onChange={(e) => updateField('dateOfBirth', e.target.value)}
                    />
                    <Input
                      label="Home Base (ICAO)"
                      value={formData.homeBase || ''}
                      onChange={(e) => updateField('homeBase', e.target.value.toUpperCase().slice(0, 4))}
                      placeholder="CYHM"
                      maxLength={4}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Medical Cat</label>
                      <select
                        value={formData.medicalCategory || ''}
                        onChange={(e) => updateField('medicalCategory', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                      >
                        <option value="">Select</option>
                        <option value="1">Cat 1</option>
                        <option value="3">Cat 3</option>
                      </select>
                    </div>
                    <Input
                      label="Medical Issue"
                      type="date"
                      value={formData.medicalIssueDate?.split('T')[0] || ''}
                      onChange={(e) => updateField('medicalIssueDate', e.target.value)}
                    />
                    <Input
                      label="Medical Expiry"
                      type="date"
                      value={formData.medicalExpiryDate?.split('T')[0] || ''}
                      onChange={(e) => updateField('medicalExpiryDate', e.target.value)}
                    />
                  </div>
                </>
              )}

              {item && (
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium">Active</label>
                  <button
                    type="button"
                    onClick={() => updateField('isActive', !formData.isActive)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isActive !== false ? 'bg-green-500' : 'bg-muted'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                      formData.isActive !== false ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className="text-sm text-muted-foreground">
                    {formData.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              )}
              {!item && formData.role !== 'pilot' && (
                <Input
                  label="Password"
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => updateField('password', e.target.value)}
                  required={!item}
                />
              )}
            </>
          )}

          {type === 'aircraft' && (
            <>
              <Input
                label="Tail Number"
                value={formData.tailNumber || ''}
                onChange={(e) => updateField('tailNumber', e.target.value.toUpperCase())}
                required
                placeholder="C-FBXL"
              />
              <Input
                label="Aircraft Type"
                value={formData.aircraftType || ''}
                onChange={(e) => updateField('aircraftType', e.target.value)}
                required
                placeholder="Citation, King Air, etc."
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Manufacturer"
                  value={formData.manufacturer || ''}
                  onChange={(e) => updateField('manufacturer', e.target.value)}
                  placeholder="Cessna"
                />
                <Input
                  label="Model"
                  value={formData.model || ''}
                  onChange={(e) => updateField('model', e.target.value)}
                  placeholder="Citation X"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Year"
                  type="number"
                  value={formData.year || ''}
                  onChange={(e) => updateField('year', parseInt(e.target.value))}
                />
                <Input
                  label="Crew Size"
                  type="number"
                  value={formData.crewSize || 2}
                  onChange={(e) => updateField('crewSize', parseInt(e.target.value))}
                />
              </div>
              {item && (
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium">Active</label>
                  <button
                    type="button"
                    onClick={() => updateField('isActive', !formData.isActive)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isActive !== false ? 'bg-green-500' : 'bg-muted'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                      formData.isActive !== false ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className="text-sm text-muted-foreground">
                    {formData.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              )}
            </>
          )}

          {type === 'trips' && (
            <>
              <Input
                label="Q-Number"
                value={formData.qNumber || ''}
                onChange={(e) => updateField('qNumber', e.target.value.toUpperCase())}
                required
                placeholder="Q-1234"
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Date"
                  type="date"
                  value={formData.startDate?.split('T')[0] || ''}
                  onChange={(e) => updateField('startDate', e.target.value)}
                  required
                />
                <Input
                  label="End Date"
                  type="date"
                  value={formData.endDate?.split('T')[0] || ''}
                  onChange={(e) => updateField('endDate', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Aircraft</label>
                <select
                  value={formData.tailNumber || ''}
                  onChange={(e) => {
                    const ac = aircraft.find(a => a.tailNumber === e.target.value);
                    updateField('tailNumber', e.target.value);
                    updateField('aircraftType', ac?.aircraftType || '');
                  }}
                  className="w-full px-3 py-2 border border-border rounded-md"
                >
                  <option value="">Select Aircraft</option>
                  {aircraft.map(ac => (
                    <option key={ac.id} value={ac.tailNumber}>
                      {ac.tailNumber} - {ac.aircraftType}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assign Crew</label>
                <select
                  multiple
                  value={formData.crewMemberIds || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, opt => parseInt(opt.value));
                    updateField('crewMemberIds', selected);
                  }}
                  className="w-full px-3 py-2 border border-border rounded-md h-32"
                >
                  {users.filter(u => u.role === 'pilot').map(pilot => (
                    <option key={pilot.id} value={pilot.id}>
                      {pilot.firstName} {pilot.lastName}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Hold Ctrl/Cmd to select multiple</p>
              </div>
              {item && (
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    value={formData.status || 'PENDING_REVIEW'}
                    onChange={(e) => updateField('status', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md"
                  >
                    <option value="PENDING_REVIEW">Pending Review</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {item ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
